import React from 'react';
import { Copy, ShieldAlert, CheckCircle2, ArrowRight, PlayCircle } from 'lucide-react';
import type { CopytradeContent } from '../../types';
import { COPYTRADE } from '../../constants/testIds';
import { toEmbedUrl } from '../../utils/video';

export function CopytradeView({ content }: { content: CopytradeContent | null }) {
  if (!content) {
    return (
      <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-10 text-center text-white/50">
        Konten Copytrade belum tersedia. Silakan cek kembali nanti.
      </div>
    );
  }

  return (
    <div data-testid={COPYTRADE.page} className="space-y-8">
      {/* Hero */}
      <div className="bg-gradient-to-br from-cyan-400/10 to-transparent border border-cyan-400/20 rounded-3xl p-8 sm:p-10">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
            <Copy className="w-4 h-4 text-cyan-400" strokeWidth={1.5} />
          </div>
          <span className="text-xs uppercase tracking-[0.2em] text-cyan-400">Copytrade Program</span>
        </div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-light text-white">{content.title}</h1>
        {content.short_description && <p className="text-white/70 mt-3 max-w-2xl">{content.short_description}</p>}
        {content.cta_label && content.cta_link && (
          <a
            href={content.cta_link}
            target="_blank"
            rel="noreferrer"
            data-testid={COPYTRADE.cta}
            className="inline-flex items-center gap-2 mt-6 bg-cyan-400 text-[#050914] font-medium px-6 py-3 rounded-lg transition-colors hover:bg-cyan-300"
          >
            {content.cta_label} <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </a>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {content.long_description && (
            <section>
              <h2 className="text-lg font-medium text-white mb-3">Tentang Program</h2>
              <p className="text-white/70 leading-relaxed whitespace-pre-line">{content.long_description}</p>
            </section>
          )}

          {content.steps && content.steps.length > 0 && (
            <section>
              <h2 className="text-lg font-medium text-white mb-4">Langkah Bergabung</h2>
              <div className="space-y-3">
                {content.steps.map((s, i) => (
                  <div key={i} className="flex items-start gap-3 bg-white/[0.02] border border-white/10 rounded-xl p-4">
                    <div className="w-7 h-7 rounded-lg bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center text-cyan-400 text-sm font-medium shrink-0">{i + 1}</div>
                    <p className="text-white/80 text-sm pt-1">{s}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {content.embed_video_url && (
            <section>
              <h2 className="text-lg font-medium text-white mb-3">Video Penjelasan</h2>
              <div data-testid={COPYTRADE.video} className="aspect-video w-full rounded-2xl border border-white/10 bg-black/40 overflow-hidden">
                <iframe src={toEmbedUrl(content.embed_video_url)} title="Copytrade" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
            </section>
          )}
        </div>

        <div className="lg:col-span-1 space-y-6">
          {content.risk_disclaimer && (
            <div className="bg-amber-400/5 border border-amber-400/20 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-2 text-amber-300">
                <ShieldAlert className="w-4 h-4" strokeWidth={1.5} />
                <h3 className="text-sm uppercase tracking-wider">Risk Disclaimer</h3>
              </div>
              <p className="text-sm text-white/60 leading-relaxed">{content.risk_disclaimer}</p>
            </div>
          )}
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6">
            <div className="flex items-center gap-2 text-cyan-400 mb-2"><CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /><span className="text-sm">Siap Bergabung?</span></div>
            <p className="text-sm text-white/60">Hubungi admin melalui tombol di atas untuk memulai proses onboarding copytrade.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
